import { Classes, Position } from '@blueprintjs/core';
// eslint-disable-next-line import/no-named-as-default, import/no-named-as-default-member
import storeLoader from 'app/stores/storeLoader';
import classNames from 'classnames';
import { Box, Button, Flex, Link, Menu, Popover, Suspense, Text } from 'core/components';
import { inject } from 'mobx-react';
import React, { Component } from 'react';
import {
  DEFAULT_FLOW_MONITORING_TEMPLATE_PRESET_ID,
  DEFAULT_MONITORING_TEMPLATE_PRESET_ID
} from '@kentik/ui-shared/nms/deviceMonitoringTemplates';
import MonitoringTemplateForm from './MonitoringTemplateForm';

@storeLoader('$metrics.monitoringTemplatesCollection')
@inject('$metrics')
export default class MonitoringTemplateSelector extends Component {
  static defaultProps = {
    label: 'Monitoring Template',
    minimal: false,
    inlineLabel: false,
    placeholder: 'Select…',
    withDefault: true,
    templateType: 'NMS' // Default to NMS type
  };

  componentDidUpdate(prevProps) {
    const { loading, model, value, withDefault, $metrics, templateType } = this.props;

    // storeLoader finished
    if (prevProps.loading && !loading) {
      const id = value || model?.get('monitoring_template_id');
      const { defaultTemplate, defaultFlowTemplate } = $metrics.monitoringTemplatesCollection;

      if (templateType === 'NMS' && withDefault && defaultTemplate && !Number(id)) {
        this.handleChange(defaultTemplate.id);
      }

      if (templateType === 'FLOW' && withDefault && defaultFlowTemplate && !Number(id)) {
        this.handleChange(defaultFlowTemplate.id);
      }
    }
  }

  state = {
    newTemplateModel: null,
    isOpen: false
  };

  get selectedTemplate() {
    const { value, model, $metrics } = this.props;
    const collection = $metrics.monitoringTemplatesCollection;
    const id = value || model?.get('monitoring_template_id');
    return id ? collection.get(id) : null;
  }

  get filteredTemplates() {
    const { $metrics, templateType } = this.props;
    const collection = $metrics.monitoringTemplatesCollection;

    // Filter templates by type
    return collection.models.filter(
      (template) => template.get('template_type') === templateType || template.get('template_type') == null
    );
  }

  get filteredPresets() {
    return this.filteredTemplates.filter((model) => model.isPreset);
  }

  get filteredCustomTemplates() {
    return this.filteredTemplates.filter((model) => !model.isPreset);
  }

  handleChange = (value) => {
    const { onChange, model } = this.props;

    if (onChange) {
      onChange(value);
    } else if (model) {
      model.set('monitoring_template_id', value);
    }
  };

  handleOpen = (isOpen = true) => {
    this.setState({ isOpen });
  };

  handleCreate = (sourceTemplate = null, isFlowTemplate = false) => {
    const { $metrics, templateType } = this.props;
    const collection = $metrics.monitoringTemplatesCollection;
    const defaultTemplateId = isFlowTemplate
      ? DEFAULT_FLOW_MONITORING_TEMPLATE_PRESET_ID
      : DEFAULT_MONITORING_TEMPLATE_PRESET_ID;
    const defaultTemplate = this.filteredTemplates.find((t) => t.get('preset') === defaultTemplateId);
    const model = sourceTemplate
      ? sourceTemplate.duplicate()
      : (defaultTemplate || collection.defaultTemplate).duplicate({
          isNew: true,
          template_type: templateType
        });

    this.setState({ newTemplateModel: model });
  };

  handleCloseTemplateDialog = () => {
    this.setState(({ newTemplateModel }) => {
      if (newTemplateModel?.isNew === false) {
        this.handleChange(newTemplateModel.id);
      }
      return { newTemplateModel: null };
    });
  };

  optionRenderer = (template, selected) => (
    <li key={template.id}>
      <Button
        className={classNames(Classes.MENU_ITEM, Classes.POPOVER_DISMISS, {
          [Classes.ACTIVE]: template.id === selected?.id
        })}
        textAlign="left"
        onClick={() => (template.id === selected?.id ? null : this.handleChange(template.id))}
        minimal
        ellipsis
      >
        <Box>{template.get('name')}</Box>
        <Text muted small>
          {template.get('description')}
        </Text>
      </Button>
    </li>
  );

  get buttonLabel() {
    const { showButtonLabel, label, loading, placeholder } = this.props;
    const { selectedTemplate } = this;
    if (showButtonLabel && label) {
      return label;
    }
    if (loading) {
      return 'Loading…';
    }
    if (!selectedTemplate) {
      return placeholder;
    }
    if (selectedTemplate) {
      return selectedTemplate.get('name');
    }
    return label;
  }

  render() {
    const { inlineLabel, loading, minimal, popoverProps, templateType } = this.props;
    const { newTemplateModel, isOpen } = this.state;
    const { selectedTemplate } = this;
    const isCreatingTemplateModel = newTemplateModel !== null;

    const templateTypeLabel = templateType === 'FLOW' ? 'Flow' : 'NMS';

    return (
      <>
        <Popover
          canEscapeKeyClose
          modifiers={{
            flip: { enabled: true, boundariesElement: 'viewport' },
            preventOverflow: { enabled: true, boundariesElement: 'viewport' },
            offset: { enabled: true, offset: '0,2px' }
          }}
          content={
            <Menu width={315} overflow="hidden" borderRadius={3}>
              <Suspense loading={loading}>
                <Box maxHeight={350} overflow="auto">
                  <li className={Classes.MENU_HEADER}>
                    <h6 className={Classes.HEADING}>Presets ({templateTypeLabel})</h6>
                  </li>
                  {this.filteredPresets.map((template) => this.optionRenderer(template, selectedTemplate))}
                  {this.filteredCustomTemplates.length > 0 && (
                    <>
                      <li className={Classes.MENU_HEADER}>
                        <h6 className={Classes.HEADING}>Your templates ({templateTypeLabel})</h6>
                      </li>
                      {this.filteredCustomTemplates.map((template) => this.optionRenderer(template, selectedTemplate))}
                    </>
                  )}
                </Box>
                <li style={{ padding: '7px' }}>
                  <Button
                    onClick={() => this.handleCreate(null, templateType)}
                    className={classNames(Classes.MENU_ITEM, Classes.POPOVER_DISMISS)}
                    icon="add"
                    rightIcon="blank"
                    text={`Create ${templateTypeLabel} Template`}
                    fill
                  />
                  <Flex mt="6px" justifyContent="center">
                    <Link to="/v4/settings/monitoring-templates" blank>
                      Manage templates
                    </Link>
                  </Flex>
                </li>
              </Suspense>
            </Menu>
          }
          isOpen={isOpen}
          onClose={() => this.handleOpen(false)}
          position={Position.BOTTOM_LEFT}
          minimal
          {...popoverProps}
        >
          <Button
            active={isOpen}
            onClick={() => this.handleOpen(!isOpen)}
            rightIcon="caret-down"
            textAlign="left"
            height={inlineLabel ? 38 : undefined}
            minWidth={145}
            minimal={minimal}
          >
            {inlineLabel && (
              <Text as="div" muted small pb="1px">
                {templateTypeLabel}
              </Text>
            )}
            <Text
              as="div"
              fontWeight={inlineLabel ? 'bold' : 'normal'}
              muted={loading || !selectedTemplate}
              small={inlineLabel}
            >
              {this.buttonLabel}
            </Text>
          </Button>
        </Popover>
        {isCreatingTemplateModel && (
          <MonitoringTemplateForm
            model={newTemplateModel}
            isDialog
            isOpen
            onClose={this.handleCloseTemplateDialog}
            templateType={templateType}
          />
        )}
      </>
    );
  }
}
